import dayjs, { type Dayjs } from "dayjs";
import type {
  IGoogleHealthCivilDateTime,
  IGoogleHealthHeartRatePayload,
  IGoogleHealthSampleTime,
  IGoogleHealthTimeInterval,
} from "./types";

/** Verifica se a data civil da API coincide com o dia selecionado no calendário. */
export function isSameCivilDay(
  civil: IGoogleHealthCivilDateTime | undefined,
  selectedDate: Dayjs
): boolean {
  const date = civil?.date;
  if (!date?.year || !date?.month || !date?.day) return false;

  return (
    selectedDate.year() === date.year &&
    selectedDate.month() + 1 === date.month &&
    selectedDate.date() === date.day
  );
}

/** Data civil do início do intervalo (preferida para passos/distância). */
export function getIntervalCivilStart(
  interval:
    | {
      civilStartTime?: IGoogleHealthCivilDateTime;
      startTime?: string;
    }
    | undefined
): IGoogleHealthCivilDateTime | undefined {
  return interval?.civilStartTime;
}

/** Data civil do fim do intervalo (preferida para sono — dia do despertar). */
export function getIntervalCivilEnd(
  interval:
    | {
      civilEndTime?: IGoogleHealthCivilDateTime;
      endTime?: string;
    }
    | undefined
): IGoogleHealthCivilDateTime | undefined {
  return interval?.civilEndTime;
}

/**
 * Sono conta no dia do despertar: `civilEndTime` quando existir;
 * senão `endTime` no fuso local do navegador (ex.: 09:28Z → manhã do dia 17 em UTC−3).
 */
export function sleepEndsOnSelectedDay(
  interval: IGoogleHealthTimeInterval | undefined,
  selectedDate: Dayjs
): boolean {
  if (!interval?.endTime) return false;

  const civilEnd = interval.civilEndTime;
  if (civilEnd) return isSameCivilDay(civilEnd, selectedDate);

  const wakeDay = dayjs(interval.endTime);
  const selectedDay = selectedDate.clone().startOf("day");
  return wakeDay.isSame(selectedDay, "day");
}

/** Amostra pontual (FC, peso, etc.) no dia civil selecionado. */
export function sampleOnSelectedDay(
  sampleTime: IGoogleHealthSampleTime | undefined,
  selectedDate: Dayjs
): boolean {
  if (!sampleTime) return false;

  const civil = sampleTime.civilTime;
  if (civil?.date) return isSameCivilDay(civil, selectedDate);

  const physical = sampleTime.physicalTime;
  if (!physical) return false;

  return dayjs(physical).isSame(selectedDate.clone().startOf("day"), "day");
}

export function heartRateSampleOnSelectedDay(
  heartRate: IGoogleHealthHeartRatePayload | undefined,
  selectedDate: Dayjs
): boolean {
  return sampleOnSelectedDay(heartRate?.sampleTime, selectedDate);
}
